import { useEffect, useState, ComponentPropsWithoutRef } from "react";
import styled from "styled-components";
import media from "styled-media-query";

import { Player } from "@lottiefiles/react-lottie-player";
import { ColorV2 } from "@adoptaunabuelo/react-components";
import animation from "../../assets/animations/confetti.json";
import HeaderNav, { HeaderProps } from "./Header";
import Footer, { FooterProps } from "./Footer";

const screenSize1: any = "1350px";
const screenSize2: any = "1024px";

const ContainerView = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	align-items: center;
`;
const Content = styled.div<{ $fullBleed?: boolean }>`
	display: flex;
	flex: 1;
	flex-direction: row;
	max-width: 1280px;
	padding: 24px 150px;
	height: 100%;
	${media.lessThan(screenSize1)`
        padding: 24px 65px;
    `}
	${media.lessThan(screenSize2)`
        padding: 24px 48px;
    `}
    ${media.lessThan("medium")`
        padding: 24px 32px;
    `}
    ${media.lessThan("small")`
        padding: 24px 24px;
    `}
	/* Solo para vistas con secciones full-bleed (width 100vw), p. ej. /donacion.
	   En movil ancla Content al viewport (junto con min-width:0 en
	   LeftViewContainer) para que esas secciones no inflen el contenedor ni
	   desborden en navegadores de dispositivo real. NO se aplica a vistas como
	   /proceso-donacion, que conservan su layout original. */
	${(p) =>
		p.$fullBleed &&
		media.lessThan("medium")`
        width: 100%;
        box-sizing: border-box;
    `}
`;
const LeftViewContainer = styled.div<{
	hidden?: boolean;
	hideMargin: boolean;
	$fullBleed?: boolean;
}>`
	display: flex;
	flex: 1;
	/* Solo con secciones full-bleed (100vw): sin min-width:0, el min-width:auto
	   por defecto de los flex items impide encogerse por debajo del ancho de su
	   contenido y, en navegadores de dispositivo real (Chrome y Safari, no en el
	   DevTools de escritorio), esas secciones lo inflan y desbordan a los hijos. */
	${(p) => p.$fullBleed && "min-width: 0;"}
	flex-direction: column;
	margin-right: ${(props) => (props.hideMargin ? "0px" : "48px")};
	opacity: ${(props) => (props.hidden ? 0 : 1)};
	transition: opacity 0.6s ease-in, transform 0.6s ease-out;
	transform: ${(props) =>
		props.hidden ? "translateY(20px)" : "translateY(0px)"};
	${(props) => media.lessThan(screenSize1)`
        margin-right: ${props.hideMargin ? 0 : "8%"};
    `}
	${(props) => media.lessThan(screenSize2)`
        margin-right: ${props.hideMargin ? 0 : "5%"};
    `}
    ${media.lessThan("medium")`
        margin-right: 0px;
    `}
`;
const RightViewContainer = styled.div<{ hidden?: boolean }>`
	position: relative;
	display: flex;
	flex-direction: column;
	opacity: ${(props) => (props.hidden ? 0 : 1)};
	transition: opacity 0.6s ease-in;
	${media.lessThan("medium")`
        display: none;
    `}
`;
const ConfettiView = styled.div<{ hidden?: boolean }>`
	display: flex;
	position: fixed;
	top: 0px;
	right: 0px;
	left: 0px;
	bottom: 0px;
	opacity: ${(props) => (props.hidden ? 0 : 1)};
	transition: opacity 0.6s ease-in;
`;

const Container = ({
	loading,
	headerProps,
	footerProps,
	RightView,
	fullBleedContent,
	...props
}: Props) => {
	const [activeHeader, setActiveHeader] = useState(false);
	const [show, setShow] = useState(false);
	const [showConfetti, setShowConfetti] = useState(false);
	const [hideConfetti, setHideConfetti] = useState(!props.showConfetti);

	useEffect(() => {
		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	useEffect(() => {
		setShow(false);
		setShowConfetti(false);
		delay(600).then(() => {
			setShow(true);
			setShowConfetti(true);
		});
	}, [loading]);

	const delay = (ms: number) =>
		new Promise((resolve) => setTimeout(resolve, ms));

	const handleScroll = (e: any) => {
		if (window.scrollY > 100) setActiveHeader(true);
		else setActiveHeader(false);
	};

	return (
		<ContainerView {...props}>
			<HeaderNav active={activeHeader} {...headerProps} />
			<Footer
				style={{ borderTop: "1px solid " + ColorV2.border.neutralSoft }}
				{...footerProps}
			/>
			{!hideConfetti && (
				<ConfettiView hidden={!showConfetti}>
					<Player
						style={{ width: "auto", height: "auto" }}
						loop={true}
						autoplay={true}
						src={animation}
						onEvent={(event) => {
							if (event === "loop") {
								setShowConfetti(false);
								delay(600).then(() => {
									setHideConfetti(true);
								});
							}
						}}
					/>
				</ConfettiView>
			)}
			<Content style={props.contentStyle} $fullBleed={fullBleedContent}>
				<LeftViewContainer
					hideMargin={RightView ? false : true}
					hidden={!show}
					$fullBleed={fullBleedContent}
				>
					{props.children}
				</LeftViewContainer>
				{RightView && (
					<RightViewContainer hidden={!show}>
						{RightView}
					</RightViewContainer>
				)}
			</Content>
		</ContainerView>
	);
};
export default Container;
export interface Props extends ComponentPropsWithoutRef<"div"> {
	loading?: boolean;
	RightView?: React.ReactNode;
	headerProps?: HeaderProps;
	showConfetti?: boolean;
	footerProps: FooterProps;
	contentStyle?: React.CSSProperties;
	/* Activa el ajuste de ancho en movil para vistas con secciones full-bleed
	   (width 100vw), p. ej. /donacion. Otras vistas lo dejan sin definir. */
	fullBleedContent?: boolean;
}
